import React, { useState, useEffect, useCallback } from 'react';
import { getTools, updateToolListNumber } from '../asset-registry/api/toolsApi';
import { useUser } from '../../shared/context/UserContext';
import './ListAssignmentPage.css';

const LIST_OPTIONS = ['NOT REVIEWED', 'LIST 1', 'LIST 2', 'LIST 3', 'LIST 4', 'LIST 5'];

export default function ListAssignmentPage() {
  const { callerEmail } = useUser();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});   // { toolId: true/false }
  const [saved, setSaved] = useState({});      // { toolId: true } for flash feedback
  const [filterOem, setFilterOem] = useState('');
  const [filterList, setFilterList] = useState('');
  const [filterSite, setFilterSite] = useState('');
  const [filterPrelim, setFilterPrelim] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [oems, setOems] = useState([]);
  const [sites, setSites] = useState([]);
  const [error, setError] = useState(null);

  const loadTools = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getTools({}, callerEmail);
      setTools(result.tools);
      setOems([...new Set(result.tools.map(t => t.oem).filter(Boolean))].sort());
      setSites([...new Set(result.tools.map(t => t.supplierName).filter(Boolean))].sort());
    } catch (err) {
      setError('Failed to load tools.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [callerEmail]);

  useEffect(() => {
    if (callerEmail) loadTools();
  }, [callerEmail, loadTools]);

  async function handleListChange(tool, newValue) {
    setSaving(prev => ({ ...prev, [tool.id]: true }));
    try {
      await updateToolListNumber(tool.id, newValue, callerEmail);
      // Update local state immediately
      setTools(prev => prev.map(t =>
        t.id === tool.id ? { ...t, listNumber: newValue } : t
      ));
      // Flash saved indicator
      setSaved(prev => ({ ...prev, [tool.id]: true }));
      setTimeout(() => setSaved(prev => ({ ...prev, [tool.id]: false })), 1500);
    } catch (err) {
      console.error('Failed to update list number:', err);
      alert(`Failed to update ${tool.toolId}. Please try again.`);
    } finally {
      setSaving(prev => ({ ...prev, [tool.id]: false }));
    }
  }

  const filtered = tools.filter(t => {
    if (filterSite && t.supplierName !== filterSite) return false;
    if (filterPrelim && (t.preliminaryToolStatus || '') !== filterPrelim) return false;
    if (filterOem && t.oem !== filterOem) return false;
    if (filterList && (t.listNumber || 'NOT REVIEWED') !== filterList) return false;
    if (filterSearch) {
      const s = filterSearch.toLowerCase();
      return (
        (t.toolId || '').toLowerCase().includes(s) ||
        (t.supplierName || '').toLowerCase().includes(s) ||
        (t.partDescription || '').toLowerCase().includes(s)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="list-assignment-page">
        <div className="la-loading">Loading tools...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="list-assignment-page">
        <div className="la-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="list-assignment-page">
      <header className="la-header">
        <div className="la-header-left">
          <h1>LIST ASSIGNMENT</h1>
          <span className="la-badge">TMG INTERNAL</span>
        </div>
        <div className="la-header-right">
          <span className="la-count">{filtered.length} of {tools.length} tools</span>
        </div>
      </header>

      <div className="la-filters">
        <input
          type="text"
          placeholder="Search tool ID, supplier, description..."
          value={filterSearch}
          onChange={e => setFilterSearch(e.target.value)}
          className="la-search"
        />
        <select
          value={filterSite}
          onChange={e => setFilterSite(e.target.value)}
          className="la-select la-select-site"
        >
          <option value="">ALL SITES</option>
          {sites.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={filterOem}
          onChange={e => setFilterOem(e.target.value)}
          className="la-select"
        >
          <option value="">ALL OEMs</option>
          {oems.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <select
          value={filterPrelim}
          onChange={e => setFilterPrelim(e.target.value)}
          className="la-select"
        >
          <option value="">ALL STATUSES</option>
          <option value="OBSOLETE">OBSOLETE</option>
          <option value="LOW MVC">LOW MVC</option>
          <option value="ACTIVE">ACTIVE</option>
        </select>
        <select
          value={filterList}
          onChange={e => setFilterList(e.target.value)}
          className="la-select"
        >
          <option value="">ALL LISTS</option>
          {LIST_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        {(filterSearch || filterOem || filterList) && (
          <button
            className="la-clear"
            onClick={() => { setFilterSearch(''); setFilterOem(''); setFilterList(''); setFilterSite(''); setFilterPrelim(''); }}
          >
            Clear
          </button>
        )}
      </div>

      <div className="la-table-wrapper">
        <table className="la-table">
          <thead>
            <tr>
              <th>TOOL ID</th>
              <th>SITE</th>
              <th>OEM</th>
              <th>TYPE</th>
              <th>LOCATION</th>
              <th>DETAIL LOC</th>
              <th>PRELIM STATUS</th>
              <th>RECON STATUS</th>
              <th className="la-col-list">LIST NUMBER</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(tool => (
              <tr key={tool.id} className={saved[tool.id] ? 'la-row-saved' : ''}>
                <td className="la-tool-id">{tool.toolId}</td>
                <td className="la-site-name">{tool.supplierName || '—'}</td>
                <td>{tool.oem || '—'}</td>
                <td>{tool.toolType || '—'}</td>
                <td>{tool.siteLoc || '—'}</td>
                <td>{tool.sectionDetail || '—'}</td>
                <td>
                  <span className={`la-prelim-badge ${(tool.preliminaryToolStatus || '').toLowerCase().replace(/\s+/g, '-')}`}>
                    {tool.preliminaryToolStatus || '—'}
                  </span>
                </td>
                <td>
                  <span className={`la-recon-badge ${(tool.reconStatus || 'open').toLowerCase()}`}>
                    {tool.reconStatus || 'OPEN'}
                  </span>
                </td>
                <td className="la-col-list">
                  <div className="la-select-wrapper">
                    <select
                      value={tool.listNumber || 'NOT REVIEWED'}
                      onChange={e => handleListChange(tool, e.target.value)}
                      disabled={saving[tool.id]}
                      className={`la-list-select ${tool.listNumber && tool.listNumber !== 'NOT REVIEWED' ? 'la-assigned' : ''}`}
                    >
                      {LIST_OPTIONS.map(l => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                    {saving[tool.id] && <span className="la-spinner">⟳</span>}
                    {saved[tool.id] && <span className="la-saved-tick">✓</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="la-empty">No tools match your filters.</div>
        )}
      </div>
    </div>
  );
}
